import React, { FC, useEffect } from "react";
import { Link, RouteComponentProps } from "react-router-dom";

type WidgetFormat = "wide" | "square" | "tall";

interface ISocialCalculatorWidgetParams {
  format?: string;
}

const formatConfig: Record<
  WidgetFormat,
  { width: number; height: number; title: string }
> = {
  wide: { width: 1024, height: 512, title: "Wide widget" },
  square: { width: 500, height: 500, title: "Square widget" },
  tall: { width: 480, height: 720, title: "Tall widget" },
};

const cardBaseStyle: React.CSSProperties = {
  position: "relative",
  borderRadius: 22,
  border: "1px solid rgba(125, 213, 255, 0.2)",
  background:
    "radial-gradient(90% 70% at 20% 20%, rgba(80, 42, 175, 0.45), transparent 62%), radial-gradient(65% 75% at 80% 88%, rgba(19, 164, 147, 0.25), transparent 70%), #050a2b",
  boxShadow: "0 24px 55px rgba(4, 7, 30, 0.6)",
  overflow: "hidden",
};

const topStripeStyle: React.CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 4,
  background: "linear-gradient(90deg, #7647ff 0%, #22d6c9 55%, #ff4b7c 100%)",
};

const moneyCardStyle: React.CSSProperties = {
  borderRadius: 14,
  border: "1px solid rgba(120, 132, 182, 0.35)",
  background: "rgba(10, 15, 55, 0.68)",
  padding: "16px 18px",
};

const logoRow = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <img src="/starfarmlogo.png" alt="StarFarm" style={{ width: 22, height: 22, objectFit: "contain" }} />
      <span style={{ color: "#ffffff", fontWeight: 700, fontStyle: "italic", fontSize: 36, letterSpacing: 0.2 }}>
        StarFarm
      </span>
    </div>
    <div style={{ color: "rgba(195, 207, 240, 0.82)", textTransform: "uppercase", letterSpacing: 2.2, fontSize: 14 }}>
      Earnings Calculator
    </div>
  </div>
);

const renderWide = () => (
  <>
    <div
      style={{
        margin: "-20px -24px 26px",
        padding: "18px 24px 16px",
        background:
          "radial-gradient(70% 100% at 10% 0%, rgba(38, 188, 181, 0.28), rgba(18, 35, 84, 0.25) 54%, rgba(11, 18, 56, 0.25)), #101f6b",
        borderBottom: "1px solid rgba(118, 182, 255, 0.3)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
        <img src="/starfarmlogo.png" alt="StarFarm logo" style={{ width: 150, height: 150, objectFit: "contain" }} />
        <div>
          <div style={{ color: "#f5f7ff", fontSize: 78, lineHeight: 1, letterSpacing: 3, fontWeight: 300 }}>
            starfarm
          </div>
          <div
            style={{
              color: "rgba(220, 228, 255, 0.6)",
              marginTop: 8,
              textTransform: "uppercase",
              letterSpacing: 9,
              fontSize: 15,
            }}
          >
            we grow stars
          </div>
        </div>
      </div>
    </div>

    <div
      style={{
        color: "#f0f3ff",
        fontSize: 54,
        lineHeight: 1.05,
        fontWeight: 700,
        fontStyle: "italic",
        marginBottom: 20,
      }}
    >
      <span style={{ color: "#1ad4c2" }}>// </span>
      Stop streaming for <span style={{ color: "#1ad4c2" }}>a fraction of a penny.</span>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 14, marginBottom: 16 }}>
      <div style={moneyCardStyle}>
        <div style={{ color: "rgba(191, 201, 228, 0.72)", textTransform: "uppercase", letterSpacing: 1.6, fontSize: 12 }}>
          Major Platforms
        </div>
        <div style={{ color: "#eef2ff", fontSize: 62, fontWeight: 700, lineHeight: 1.1 }}>$300</div>
        <div style={{ color: "rgba(171, 179, 205, 0.82)", fontSize: 14 }}>Paid 3–9 months later</div>
      </div>
      <div style={{ alignSelf: "center", color: "#ffffff", opacity: 0.8, fontWeight: 700, fontStyle: "italic", fontSize: 26 }}>
        vs
      </div>
      <div
        style={{
          ...moneyCardStyle,
          borderColor: "rgba(26, 211, 194, 0.45)",
          background: "linear-gradient(135deg, rgba(9, 30, 65, 0.85), rgba(16, 86, 98, 0.45))",
        }}
      >
        <div style={{ color: "#2fe8d4", textTransform: "uppercase", letterSpacing: 1.6, fontSize: 12 }}>StarFarm</div>
        <div style={{ color: "#22e5cf", fontSize: 62, fontWeight: 700, lineHeight: 1.1 }}>$3,000</div>
        <div style={{ color: "rgba(34, 229, 207, 0.86)", fontSize: 14 }}>Paid instantly, every play</div>
      </div>
    </div>

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
      <div style={{ color: "#d2daf7", fontSize: 34, fontWeight: 800, fontStyle: "italic", lineHeight: 1 }}>
        <span style={{ color: "#22e5cf" }}>10×</span>{" "}
        <span style={{ fontSize: 30, fontWeight: 400 }}>more in your pocket</span>{" "}
        <span style={{ fontSize: 34, fontWeight: 400, opacity: 0.9 }}>— on 100,000 plays</span>
      </div>
      <div style={{ color: "rgba(222, 232, 255, 0.94)", fontSize: 30 }}>starfarm.com/calculator</div>
    </div>
  </>
);

const renderSquareOrTall = (isTall: boolean) => (
  <>
    {logoRow()}

    <div
      style={{
        color: "#f0f3ff",
        fontSize: isTall ? 60 : 50,
        lineHeight: 1.04,
        fontWeight: 700,
        fontStyle: "italic",
        marginTop: 14,
        marginBottom: 18,
      }}
    >
      <span style={{ color: "#1ad4c2" }}>// </span>
      Stop streaming for <span style={{ color: "#1ad4c2" }}>a fraction of a penny.</span>
    </div>

    <div style={{ color: "rgba(181, 193, 224, 0.9)", textTransform: "uppercase", letterSpacing: 2, fontSize: 12, marginBottom: 6 }}>
      Plays
    </div>
    <div style={{ color: "#ffffff", fontSize: 52, fontWeight: 700, fontStyle: "italic", textAlign: "right", marginBottom: 10 }}>
      100,000
    </div>
    <div
      style={{
        height: 8,
        borderRadius: 999,
        background: "linear-gradient(90deg, #7a4aff, #24ddd1)",
        marginBottom: 16,
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: "48%",
          top: "50%",
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "#d9ffff",
          transform: "translate(-50%, -50%)",
          boxShadow: "0 0 0 6px rgba(26, 212, 194, 0.25)",
        }}
      />
    </div>

    <div style={{ display: "grid", gridTemplateColumns: isTall ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 16 }}>
      <div style={moneyCardStyle}>
        <div style={{ color: "rgba(191, 201, 228, 0.72)", textTransform: "uppercase", letterSpacing: 1.6, fontSize: 11 }}>Major Platforms</div>
        <div style={{ color: "#eef2ff", fontSize: 58, fontWeight: 700, lineHeight: 1.1 }}>$300</div>
        <div style={{ color: "rgba(171, 179, 205, 0.82)", fontSize: 13 }}>Paid 3–9 months later</div>
      </div>
      <div style={{ ...moneyCardStyle, borderColor: "rgba(26, 211, 194, 0.45)", background: "linear-gradient(135deg, rgba(9, 30, 65, 0.85), rgba(16, 86, 98, 0.45))" }}>
        <div style={{ color: "#2fe8d4", textTransform: "uppercase", letterSpacing: 1.6, fontSize: 11 }}>StarFarm</div>
        <div style={{ color: "#22e5cf", fontSize: 58, fontWeight: 700, lineHeight: 1.1 }}>$3,000</div>
        <div style={{ color: "rgba(34, 229, 207, 0.86)", fontSize: 13 }}>Paid instantly, every play</div>
      </div>
    </div>

    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
      <div style={{ color: "#d2daf7", fontSize: 42, fontWeight: 800, fontStyle: "italic", lineHeight: 1 }}>
        <span style={{ color: "#22e5cf" }}>10×</span> <span style={{ fontSize: 26, fontWeight: 400 }}>more in your pocket</span>
      </div>
      <Link
        to="/calculator"
        style={{
          textDecoration: "none",
          color: "#022635",
          backgroundColor: "#21dfcc",
          borderRadius: 999,
          padding: "13px 22px",
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: 0.3,
        }}
      >
        Full calc →
      </Link>
    </div>
  </>
);

const SocialCalculatorWidgetPage: FC<RouteComponentProps<ISocialCalculatorWidgetParams>> = ({
  match,
}) => {
  const unsafeFormat = (match.params.format || "wide").toLowerCase();
  const format: WidgetFormat = unsafeFormat === "square" || unsafeFormat === "tall" ? unsafeFormat : "wide";
  const config = formatConfig[format];

  useEffect(() => {
    document.title = `StarFarm calculator widget (${config.title.toLowerCase()})`;
  }, [config.title]);

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        margin: 0,
        padding: "18px",
        backgroundColor: "#030824",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          ...cardBaseStyle,
          width: config.width,
          minHeight: config.height,
          padding: format === "wide" ? "20px 32px 22px" : format === "square" ? "20px 20px 18px" : "22px 22px 24px",
        }}
      >
        <div style={topStripeStyle} />
        {format === "wide" ? renderWide() : renderSquareOrTall(format === "tall")}
      </div>
    </div>
  );
};

export default SocialCalculatorWidgetPage;
